/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { isChannelTypeB2B } from '@msdyn365-commerce/core';
import { BusinessPartnerUsersDataActions } from '@msdyn365-commerce/retail-proxy';
import {
    AccountStatementRequest,
    BusinessPartnerOperationDeliveryType,
    CustomerBalances
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    getTelemetryObject,
    Heading,
    ITelemetryContent,
    toRecipientEmailsByOrganizationAccounts
} from '@msdyn365-commerce-modules/utilities';
import { B2bPermission, isB2bActionPermitted } from '@msdyn365-commerce-modules/retail-actions';
import { get } from 'lodash';
import { computed } from 'mobx';
import * as React from 'react';

import { IAccountCustomerBalanceData } from './account-customer-balance.data';
import { HeadingTag, IAccountCustomerBalanceProps } from './account-customer-balance.props.autogenerated';
import {
    AccountLabels,
    AccountValues,
    ActionBar,
    AdminRequestStatementButton,
    AvailableCreditComponent,
    BackButton,
    BalanceOwedComponent,
    CreditExcessComponent,
    RequestStatementModal
} from './components';

export interface IAccountCustomerBalanceViewProps extends IAccountCustomerBalanceProps<IAccountCustomerBalanceData> {
    heading: string;
    homeButton: React.ReactNode;
    accountSectionHeading: React.ReactNode;
    accountLabels: React.ReactNode;
    accountValues: React.ReactNode;
    creditSummarySectionHeading: React.ReactNode;
    creditSummaryLabels?: React.ReactNode;
    creditSummaryValues?: React.ReactNode;
    actions: React.ReactNode;
    requestStatementModal: React.ReactNode;
    availableCredit?: React.ReactNode;
    balanceOwed?: React.ReactNode;
    creditExcess?: React.ReactNode;
}

export interface IAccountCustomerBalanceState {
    isStatementRequestModalOpen: boolean;
    statementRequestFromDateString: string;
    statementRequestToDateString: string;
    isStatementRequestAdminDropdownOpen: boolean;
    statementRequestFullOrg: boolean;
    statementRequestMaxDateString?: string;
    recipientEmails: string[];
    selectedRecipientEmail: string;
}

/**
 *
 * AccountCustomerBalance component.
 * @extends {React.PureComponent<IAccountCustomerBalanceProps<IAccountCustomerBalanceData>>}
 */
class AccountCustomerBalance extends React.PureComponent<
    IAccountCustomerBalanceProps<IAccountCustomerBalanceData>,
    IAccountCustomerBalanceState
> {
    private readonly telemetryContent?: ITelemetryContent;
    private readonly requestStatementButtonRef: React.RefObject<HTMLButtonElement> = React.createRef<HTMLButtonElement>();

    @computed get canSubmitStatementRequest(): boolean {
        return this.state.statementRequestFromDateString !== '' && this.state.statementRequestToDateString !== '';
    }

    constructor(props: IAccountCustomerBalanceProps<IAccountCustomerBalanceData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.state = {
            isStatementRequestModalOpen: false,
            statementRequestFromDateString: '',
            statementRequestToDateString: '',
            isStatementRequestAdminDropdownOpen: false,
            statementRequestFullOrg: false,
            statementRequestMaxDateString: new Date().toISOString().split('T')[0],
            recipientEmails: [],
            selectedRecipientEmail: ''
        };
    }

    public render(): JSX.Element | null {
        const customer = get(this.props, 'data.customerInformation.result');
        const account: CustomerBalances = get(this.props, 'data.balance.result');
        if ([customer, account].includes(undefined)) {
            this.props.context.telemetry.error('Customer data is unavailable, module will not render');
            return null;
        }

        const { config, resources, context } = this.props;
        const { heading } = config;
        const {
            creditSummarySectionHeading,
            accountSectionHeading,
            backToShoppingText,
            customerSincePrefix,
            availableCreditLabel,
            balanceOwedLabel,
            creditExcessLabel,
            requestStatementButtonLabel,
            modalHeaderLabel,
            sendToEmailLabel,
            fromDateLabel,
            toDateLabel,
            submitRequestLabel,
            cancelLabel,
            myAccountStatementLabel,
            fullOrganizationLabel,
            modalAriaLabel
        } = resources;

        const {
            isStatementRequestModalOpen,
            statementRequestFromDateString,
            statementRequestToDateString,
            statementRequestMaxDateString
        } = this.state;

        const accountNumber = customer.AccountNumber;
        const createdOn = this._formatJoinDate(new Date(customer.CreatedDateTime));
        const name = `${customer.FirstName} ${customer.LastName}`;
        let customerCredit = 0;
        let customerAccountBalance = 0;
        let excess = 0;

        if (account.InvoiceAccountCreditLimit === 0) {
            customerCredit = account.CreditLimit - account.Balance;
            customerAccountBalance = account.Balance;
            excess = account.Balance - account.CreditLimit;
        } else {
            customerCredit = account.InvoiceAccountCreditLimit - account.InvoiceAccountBalance;
            customerAccountBalance = account.InvoiceAccountBalance;
            excess = account.InvoiceAccountBalance - account.InvoiceAccountCreditLimit;
        }

        const creditExcess = excess > 0 ? this._formatCurrency(excess) : null;
        const availableCredit = this._formatCurrency(customerCredit);
        const balanceOwed = this._formatCurrency(customerAccountBalance);
        const backLink = Msdyn365.getUrlSync('home', context.actionContext) || '';
        const emailAddress = customer.Email;
        const isB2BSite = isChannelTypeB2B(this.props.context.request);
        // Requesting an account statement requires the AllowOnAccountUse permission; when the B2BContact
        // permission model is off this falls back to the legacy B2B admin check.
        const canRequestStatement =
            isB2BSite &&
            isB2bActionPermitted({
                isB2bContact: this.props.context.request.user.isB2bContact,
                isLegacyAdmin: customer.IsB2bAdmin,
                permissions: this.props.data.b2bUserInformation?.result?.Permissions,
                required: B2bPermission.AllowOnAccountUse
            });

        const actionBar = canRequestStatement ? (
            <AdminRequestStatementButton
                buttonReference={this.requestStatementButtonRef}
                className='ms-account-customer-balance'
                onRequestStatement={this._adminShowRequestStatementModal}
                requestStatementButtonLabel={requestStatementButtonLabel}
                onToggleDropdown={this._toggleDropdown}
                myUserAccountLabel={myAccountStatementLabel}
                fullOrganizationLabel={fullOrganizationLabel}
                isDropdownShowing={this.state.isStatementRequestAdminDropdownOpen}
            />
        ) : (
            isB2BSite && (
                <ActionBar
                    buttonReference={this.requestStatementButtonRef}
                    className='ms-account-customer-balance'
                    onRequestStatement={this._showRequestStatementModal}
                    requestStatementButtonLabel={requestStatementButtonLabel}
                />
            )
        );

        const viewProps = {
            ...this.props,
            heading: <Heading text={heading.text} tag={heading.tag} />,
            accountSectionHeading: (
                <Heading tag={HeadingTag.h3} text={accountSectionHeading} className='ms-account-customer-balance__account-heading' />
            ),
            accountLabels: (
                <AccountLabels name={name} accountNumber={accountNumber} createdOn={createdOn} customerSincePrefix={customerSincePrefix} />
            ),
            accountValues: <AccountValues availableCredit={availableCredit} availableCreditLabel={availableCreditLabel} />,
            creditSummarySectionHeading: (
                <Heading
                    tag={HeadingTag.h3}
                    text={creditSummarySectionHeading}
                    className='ms-account-customer-balance__credit-summary-heading'
                />
            ),
            availableCredit: <AvailableCreditComponent availableCreditLabel={availableCreditLabel} availableCredit={availableCredit} />,
            balanceOwed: <BalanceOwedComponent balanceOwedLabel={balanceOwedLabel} balanceOwed={balanceOwed} />,
            creditExcess: <CreditExcessComponent creditExcessLabel={creditExcessLabel} creditExcess={creditExcess} />,
            homeButton: <BackButton label={backToShoppingText} link={backLink} telemetryContent={this.telemetryContent} />,
            actions: actionBar,
            requestStatementModal: isB2BSite && (
                <RequestStatementModal
                    email={emailAddress}
                    isModalOpen={isStatementRequestModalOpen}
                    toggle={this._toggleModal}
                    fromDateString={statementRequestFromDateString}
                    toDateString={statementRequestToDateString}
                    onChangeFromDate={this._updateFromDate}
                    onChangeToDate={this._updateToDate}
                    canSubmitRequest={this.canSubmitStatementRequest}
                    modalHeaderLabel={modalHeaderLabel}
                    sendToEmailLabel={sendToEmailLabel}
                    fromDateLabel={fromDateLabel}
                    toDateLabel={toDateLabel}
                    submitRequestLabel={submitRequestLabel}
                    cancelLabel={cancelLabel}
                    onSubmitRequest={this._requestStatement}
                    className='ms-account-customer-balance'
                    selectedUser=''
                    selectedUserLabel=''
                    modalAriaLabel={modalAriaLabel}
                    maxDateString={statementRequestMaxDateString}
                    recipientEmails={this.state.recipientEmails}
                    selectedRecipientEmail={this.state.selectedRecipientEmail}
                    onChangeRecipientEmail={this._updateRecipientEmail}
                />
            )
        };

        return this.props.renderView(viewProps);
    }

    private _formatCurrency(currency: number): string {
        const currencyCode = get(this.props, 'context.request.channel.Currency');
        const formatCurrency = get(this.props, 'context.cultureFormatter.formatCurrency');

        return `${formatCurrency(currency.toFixed(2), currencyCode)}`;
    }

    private _formatJoinDate(date: Date): string {
        const i14nDate = new Intl.DateTimeFormat(this.props.context.actionContext.requestContext.locale, {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });

        return i14nDate.format(date);
    }

    private readonly _showRequestStatementModal = (): void => {
        if (!this.state.isStatementRequestModalOpen) {
            void this._loadRecipientEmails();
            this.setState({
                isStatementRequestModalOpen: true
            });
        }
    };

    // Self only outside the contact model (today's behavior); the org's accessible contacts under it.
    private readonly _loadRecipientEmails = async (): Promise<void> => {
        const customer = this.props.data.customerInformation.result;
        const selfEmail = customer?.Email?.trim() ?? '';
        const isB2bContact = this.props.context.request.user?.isB2bContact === true;

        if (!isB2bContact) {
            this.setState({ recipientEmails: selfEmail ? [selfEmail] : [], selectedRecipientEmail: selfEmail });
            return;
        }

        try {
            const b2bUserInformation = this.props.data.b2bUserInformation;
            const b2bUser = b2bUserInformation?.result ?? (b2bUserInformation ? await b2bUserInformation : undefined);
            const organizationAccountNumber = b2bUser?.OrganizationCustomerAccountNumber;
            const users = await BusinessPartnerUsersDataActions.getAccessibleUsersAsync({
                callerContext: this.props.context.actionContext
            });
            const emails = toRecipientEmailsByOrganizationAccounts(
                users,
                organizationAccountNumber ? [organizationAccountNumber] : [],
                selfEmail
            );
            // Default to self explicitly rather than emails[0], so the default never relies on array ordering.
            this.setState({ recipientEmails: emails, selectedRecipientEmail: selfEmail || emails[0] || '' });
        } catch (error) {
            this.props.context.telemetry.error(`Failed to load accessible recipient emails: ${error}`);
            this.setState({ recipientEmails: selfEmail ? [selfEmail] : [], selectedRecipientEmail: selfEmail });
        }
    };

    private readonly _updateRecipientEmail = (email: string): void => {
        this.setState({ selectedRecipientEmail: email });
    };

    private readonly _toggleModal = (): void => {
        this.setState({
            isStatementRequestModalOpen: false
        });
        this.requestStatementButtonRef.current?.focus();
    };

    private readonly _toggleDropdown = (): void => {
        this.setState({
            isStatementRequestAdminDropdownOpen: !this.state.isStatementRequestAdminDropdownOpen
        });
    };

    private readonly _updateFromDate = (newFromDate: string) => {
        this.setState({
            statementRequestFromDateString: newFromDate
        });
    };

    private readonly _updateToDate = (newToDate: string) => {
        this.setState({
            statementRequestToDateString: newToDate
        });
    };

    private readonly _adminShowRequestStatementModal = (isFullOrg: boolean) => {
        this.setState({
            statementRequestFullOrg: isFullOrg,
            isStatementRequestAdminDropdownOpen: false
        });

        this._showRequestStatementModal();
    };

    private readonly _requestStatement = () => {
        const customer = this.props.data.customerInformation.result!;
        const b2bUserInfo = this.props.data.b2bUserInformation?.result;
        const isB2bContact = this.props.context.request.user?.isB2bContact === true;
        const requestedUserId = (isB2bContact ? b2bUserInfo?.ContactPersonId : b2bUserInfo?.B2BUserId) || '';

        const statementRequest: AccountStatementRequest = {
            FromDate: new Date(this.state.statementRequestFromDateString),
            ToDate: new Date(this.state.statementRequestToDateString),
            IsForFullOrganization: this.state.statementRequestFullOrg,
            RequestedUserId: requestedUserId,
            RequestingUserEmail: this.state.selectedRecipientEmail || customer.Email,
            DeliveryTypeValue: BusinessPartnerOperationDeliveryType.Email
        };

        const context = { callerContext: this.props.context.actionContext };

        BusinessPartnerUsersDataActions.requestAccountStatementAsync(context, requestedUserId, statementRequest);

        this.setState({
            isStatementRequestModalOpen: false
        });

        this.requestStatementButtonRef.current?.focus();
    };
}

export default AccountCustomerBalance;
